import { useState, useEffect } from 'react';
import { customersApi } from '../../../services/api';
import { Customer } from '../../../types';

export const useCustomers = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async (page?: number, perPage?: number, search?: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await customersApi.getAll({
        page: page || currentPage,
        per_page: perPage || itemsPerPage,
        search: search !== undefined ? search : debouncedSearchQuery,
      });
      setCustomers(res.data);
      setTotalPages(res.last_page || 1);
      setTotal(res.total || 0);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load data when page, itemsPerPage, or debouncedSearchQuery changes
  useEffect(() => {
    load(currentPage, itemsPerPage, debouncedSearchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, debouncedSearchQuery]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (searchQuery !== debouncedSearchQuery) {
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return {
    customers,
    loading,
    error,
    setError,
    searchQuery,
    setSearchQuery,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    total,
    load,
    handlePageChange,
  };
};
